import { AsYouType, parsePhoneNumberFromString } from 'libphonenumber-js';
import type { CountryCode } from 'libphonenumber-js';

export interface WhitelistedCountry {
  country_code: string;
  self_buy: boolean;
  price: number;
}

export interface PhoneNumberChargeDetail {
  enabled: boolean;
  unit_amount: number;
}

export type PhoneNumberPurchaseAccess = 'default' | 'self_buy' | 'contact_support';

export function normalizeCountryCode(code?: string | null): string {
  return (
    code
      ?.toString()
      .trim()
      .toUpperCase()
      .replace(/[^A-Z]/g, '')
      .slice(0, 2) ?? ''
  );
}

/**
 * ISO country from a phone string (e.g. +1213… → US, +1416… → CA).
 * Incomplete / invalid numbers still resolve when the dialing prefix is known
 * (TelInput often has +1 before a full national number is typed).
 */
export function countryCodeFromPhoneNumber(phone?: string | null, fallback?: string | null): string | undefined {
  const fallbackCode = normalizeCountryCode(fallback) || undefined;
  const fallbackCountry = fallbackCode as CountryCode | undefined;
  const raw = phone?.toString().trim();
  if (!raw) return fallbackCode;

  const parsed =
    parsePhoneNumberFromString(raw) || (fallbackCountry ? parsePhoneNumberFromString(raw, fallbackCountry) : undefined);
  const fromParsed = normalizeCountryCode(parsed?.country);
  if (fromParsed) return fromParsed;

  const digits = raw.replace(/[^\d+]/g, '');
  if (digits.startsWith('+')) {
    const asYouType = new AsYouType(fallbackCountry);
    asYouType.input(digits);
    const fromTyping = normalizeCountryCode(asYouType.getCountry());
    if (fromTyping) return fromTyping;

    // NANP (+1) without enough digits to distinguish US vs CA — prefer TelInput default.
    if (asYouType.getCallingCode() === '1' || /^\+1\d*$/.test(digits)) {
      return fallbackCode || 'US';
    }
  } else if (fallbackCountry) {
    const asYouType = new AsYouType(fallbackCountry);
    asYouType.input(digits);
    const fromTyping = normalizeCountryCode(asYouType.getCountry());
    if (fromTyping) return fromTyping;
  }

  return fallbackCode;
}

export function findWhitelistedCountry(
  countries: WhitelistedCountry[] | undefined,
  countryCode: string
): WhitelistedCountry | undefined {
  const normalized = normalizeCountryCode(countryCode);
  if (!normalized) return undefined;

  return countries?.find((row) => normalizeCountryCode(row.country_code) === normalized);
}

export function getPhoneNumberPurchaseAccess(countryConfig: WhitelistedCountry | undefined): PhoneNumberPurchaseAccess {
  if (!countryConfig) return 'default';
  if (!countryConfig.self_buy) return 'contact_support';
  return 'self_buy';
}

export function isPhoneNumberChargeEnabled(priceDetails?: { phone_number_charge?: PhoneNumberChargeDetail }): boolean {
  return priceDetails?.phone_number_charge?.enabled === true;
}

export function getPhoneNumberSurchargeAmount(priceDetails?: {
  phone_number_charge?: PhoneNumberChargeDetail;
}): number {
  if (!isPhoneNumberChargeEnabled(priceDetails)) return 0;
  return Number(priceDetails?.phone_number_charge?.unit_amount ?? 0);
}

export interface PhoneNumberUnitAmountGroup {
  quantity: number;
  unit_amount: number;
}

export function formatPhoneNumberChargesQuantityLabel(
  usage?: {
    quantity?: number;
    unit_amount?: number | null;
    unit_amount_groups?: PhoneNumberUnitAmountGroup[];
  },
  formatAmount: (amount?: string | number, decimalCount?: number) => string = (amount) =>
    `$${Number(amount || 0).toFixed(2)}`
): string {
  const groups = usage?.unit_amount_groups?.filter((group) => group.quantity > 0) ?? [];
  if (groups.length > 0) {
    return groups.map((group) => `${group.quantity} x ${formatAmount(group.unit_amount, 9)}`).join(' + ');
  }

  return `${usage?.quantity ?? 0} x ${formatAmount(usage?.unit_amount ?? undefined, 9)}`;
}
